import * as THREE from 'three';
import { CELL } from '../core/config';
import type { Cell } from '../world/city';
import type { GeometryBuilder } from './geometry';

type Point = [number, number, number];
const FACES: Point[][] = [
  [
    [1, 0, 0],
    [1, 1, 0],
    [1, 1, 1],
    [1, 0, 1],
  ],
  [
    [0, 0, 1],
    [0, 1, 1],
    [0, 1, 0],
    [0, 0, 0],
  ],
  [
    [0, 1, 0],
    [0, 1, 1],
    [1, 1, 1],
    [1, 1, 0],
  ],
  [
    [0, 0, 1],
    [0, 0, 0],
    [1, 0, 0],
    [1, 0, 1],
  ],
  [
    [1, 0, 1],
    [1, 1, 1],
    [0, 1, 1],
    [0, 0, 1],
  ],
  [
    [0, 0, 0],
    [0, 1, 0],
    [1, 1, 0],
    [1, 0, 0],
  ],
];

/** A clipped convex template supplies real cut faces and hard normals. The
 * exact occupied cuboid is a conservative collision approximation of its small
 * chipped corner; thin slab/pane/beam dimensions come from authoritative bounds.
 */
export function fractureGeometry(
  g: GeometryBuilder,
  cell: Cell,
  x: number,
  y: number,
  z: number,
  color: number,
  visible: boolean[],
): void {
  const b = cell.bounds ?? { x: 0, y: 0, z: 0, w: 1, h: 1, d: 1 };
  const seed = (cell.id * 1664525 + (cell.fracture ?? 1) * 1013904223) >>> 0;
  const signs = [seed & 1 ? 1 : -1, seed & 2 ? 1 : -1, seed & 4 ? 1 : -1];
  const cut =
    cell.material === 'glass'
      ? 0.45
      : cell.material === 'brick'
        ? 0.25
        : cell.material === 'wood'
          ? 0.2
          : 0.28 + (seed % 5) * 0.035;
  const distance = (p: Point) => 3 - cut - p.reduce((sum, n, i) => sum + (signs[i] > 0 ? n : 1 - n), 0);
  const intersections: Point[] = [];
  const point = (p: Point) => ({
    x: x + (b.x + p[0] * b.w) * CELL,
    y: y + (b.y + p[1] * b.h) * CELL,
    z: z + (b.z + p[2] * b.d) * CELL,
  });
  const emit = (polygon: Point[], tint: number) => {
    if (polygon.length < 3) return;
    for (let i = 1; i < polygon.length - 1; i++) {
      const a = point(polygon[0]),
        bb = point(polygon[i]),
        c = point(polygon[i + 1]);
      const normal = new THREE.Vector3(bb.x - a.x, bb.y - a.y, bb.z - a.z)
        .cross(new THREE.Vector3(c.x - a.x, c.y - a.y, c.z - a.z))
        .normalize();
      g.triangle(a, bb, c, normal, tint);
    }
  };
  for (let face = 0; face < 6; face++) {
    const polygon = FACES[face],
      clipped: Point[] = [];
    for (let i = 0; i < polygon.length; i++) {
      const a = polygon[i],
        bb = polygon[(i + 1) % polygon.length],
        da = distance(a),
        db = distance(bb);
      if (da >= 0) clipped.push(a);
      if (da >= 0 !== db >= 0) {
        const t = da / (da - db);
        const p: Point = [a[0] + (bb[0] - a[0]) * t, a[1] + (bb[1] - a[1]) * t, a[2] + (bb[2] - a[2]) * t];
        clipped.push(p);
        if (!intersections.some((q) => Math.hypot(q[0] - p[0], q[1] - p[1], q[2] - p[2]) < 1e-6))
          intersections.push(p);
      }
    }
    if (visible[face]) emit(clipped, color);
  }
  if (intersections.length >= 3) {
    const center = intersections
      .reduce((s, p) => s.add(new THREE.Vector3(...p)), new THREE.Vector3())
      .divideScalar(intersections.length);
    const normal = new THREE.Vector3(...signs).normalize();
    const u = new THREE.Vector3(1, 0, 0).cross(normal).normalize(),
      v = normal.clone().cross(u);
    intersections.sort((a, bb) => {
      const pa = new THREE.Vector3(...a).sub(center),
        pb = new THREE.Vector3(...bb).sub(center);
      return Math.atan2(pa.dot(v), pa.dot(u)) - Math.atan2(pb.dot(v), pb.dot(u));
    });
    const tint = new THREE.Color(color)
      .multiplyScalar(cell.material === 'wood' ? 1.13 : cell.material === 'glass' ? 1.2 : 0.79)
      .getHex();
    emit(intersections, tint);
  }
}

export function coveredFace(a: Cell, b: Cell | undefined, direction: number[]): boolean {
  if (!b) return false;
  const aa = a.bounds,
    bb = b.bounds;
  const ax = aa?.x ?? 0,
    ay = aa?.y ?? 0,
    az = aa?.z ?? 0,
    bx = (bb?.x ?? 0) + direction[0],
    by = (bb?.y ?? 0) + direction[1],
    bz = (bb?.z ?? 0) + direction[2];
  // These are the same occupied-face comparisons without five temporary
  // arrays per neighbour. A blast evaluates hundreds of thousands of faces.
  return (
    coveredAxis(ax, ax + (aa?.w ?? 1), bx, bx + (bb?.w ?? 1), direction[0]) &&
    coveredAxis(ay, ay + (aa?.h ?? 1), by, by + (bb?.h ?? 1), direction[1]) &&
    coveredAxis(az, az + (aa?.d ?? 1), bz, bz + (bb?.d ?? 1), direction[2])
  );
}

function coveredAxis(a: number, A: number, b: number, B: number, direction: number): boolean {
  return direction ? Math.abs(direction > 0 ? A - b : a - B) <= 1e-7 : b <= a + 1e-7 && B >= A - 1e-7;
}
